import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

/**
 * Service for cost code related API calls.
 */
export default Service.extend({
  api: service(),
  store: service(),
  shData: service(),

  /**
   * Create a new cost code
   *
   * @param {Company} company
   * @param {String} body
   * @return {Ember.RSVP.Promise}
   */
  createCostCode(company, body) {
    let options = {
      jsonData: {
        'company_id': company.get('id'),
        'body' : body
      }
    };
    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').post(`cost-codes/`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let costCode = this.get('store').peekRecord('costCode', response.data.id);
        resolve(costCode);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error creating your cost code. Please refresh and try again.',
          'createCostCode -- error creating cost code.',
        );
        reject(e);
      });
    });
  },

  /**
   * Delete a given cost code
   *
   * @param {CostCode} costCode
   * @return {Ember.RSVP.Promise}
   */
  deleteCostCode(costCode) {
    let options = {};
    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').delete(`cost-codes/${costCode.get('id')}/`, options).then(() => {
        resolve(true);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error deleting your cost code. Please refresh and try again.',
          'deleteCostCode -- error deleting cost code. code id: ' + costCode.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Get the cost codes for a company.
   *
   * @param {Company} company
   * @param {Array<String>=} opt_includes
   * @param {Object=} opt_filters     Additional filters to add.
   * @return {Ember.RSVP.Promise}
   */
  getCostCodesForCompany(company, opt_includes = [], opt_filters = {}, page = null, count = null) {
    let options = {
      params : {
        filter : {
          company_id : company.id,
          ...opt_filters,
        }
      }
    };

    if (page) {
      options.params['page'] = page;
    }

    if (count) {
      options.params['count'] = count;
    }

    this.shData.addIncludesToOptions(options, opt_includes);

    return this.api.request('cost-codes/', options).then(response => {
      this.shData.pushResponseToStore(response);

      // paginated response object
      if (page) {
        return {
          response: this.shData.getModelArrayFromResponse(response, 'cost-code'),
          pagination: this.shData.getPaginationModelFromResponse(response),
        }
      }

      return this.shData.getModelArrayFromResponse(response, 'cost-code');
    });
  },

  /**
   * Get a cost code by id
   *
   * @param {CostCode} costCode
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  getCostCodeById(codeId, opt_includes = []) {
    const options = {};
    this.shData.addIncludesToOptions(options, opt_includes);

    return this.api.get(`cost-codes/${codeId}`, options).then(response => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('costCode', response.data.id);
    });
  },

  /**
   * Update a cost code
   *
   * @param {CostCode} costCode
   * @param {Boolean|null} opt_deactivated
   * @param {Boolean|null} opt_adminOnly
   * @return {Ember.RSVP.Promise}
   */
  updateCostCode(costCode, opt_deactivated = null, opt_adminOnly = null) {
    let options = {
      jsonData: {}
    };

    if (opt_deactivated != null) options.jsonData['deactivated'] = opt_deactivated;
    if (opt_adminOnly != null) options.jsonData['admin_only'] = opt_adminOnly;

    this.shData.addIncludesToOptions(options, []);

    return this.api.put(`cost-codes/${costCode.id}`, options);
  },

  /**
   * Transfer an available credit amount between cost codes.
   *
   * @param {Number} sourceCostCodeId
   * @param {Number} destinationCostCodeId
   * @param {Number} amountInCents
   * @param {String} integrityChecksum
   * @param {String=} memo
   * @returns
   */
  transferCreditToCostCode(
    sourceCostCodeId,
    destinationCostCodeId,
    amountInCents,
    integrityChecksum,
    memo = '',
  ) {
    const options = {
      jsonData: {
        amount_in_cents: amountInCents,
        integrity_checksum: integrityChecksum,
        target_cost_code_id: destinationCostCodeId,
      },
    };

    if (memo) {
      options.jsonData['memo'] = memo;
    }

    return this.api.put(`cost-codes/${sourceCostCodeId}/transfer-credit-to-cost-code`, options);
  },

  /**
   * Transfer credit from a cost code to an existing CompanyCreditTransferTarget model
   *
   * @param {Number} sourceCostCodeId
   * @param {Number} amountInCents
   * @param {String} integrityChecksum
   * @param {Number} companyCreditTransferTargetId
   * @param {String} companyCreditTransferTargetType
   * @param {String} transactionDate
   * @returns
   */
  transferCredit(
    sourceCostCodeId,
    amountInCents,
    integrityChecksum,
    companyCreditTransferTargetId,
    companyCreditTransferTargetType,
    transactionDate = '',
  ) {
    const options = {
      jsonData: {
        amount_in_cents: amountInCents,
        integrity_checksum: integrityChecksum,
        company_credit_transfer_target_id: companyCreditTransferTargetId,
        company_credit_transfer_target_type: companyCreditTransferTargetType,
        transaction_date: transactionDate,
      },
    };

    return this.api.put(`cost-codes/${sourceCostCodeId}/transfer-credit`, options);
  },
});
